// AlertsCard — who gets told when a payout rail stops answering.
//
// Admin-only (the routes 403 otherwise). One card with three parts: the
// recipient list with add / mute / remove, the rails the monitor is
// watching with their last settled state, and two actions — a test send
// so a new address can be confirmed on the spot, and check-now so an
// operator does not wait a minute to see the monitor's verdict.
//
// Rails are named through providerLabel(); the backend never sends the
// upstream host and this card must never show one.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addAlertChannel,
  addAlertRecipient,
  deleteAlertChannel,
  deleteAlertRecipient,
  getAlertsOverview,
  runAlertCheck,
  sendAlertTest,
  setAlertChannelEnabled,
  setAlertRecipientEnabled,
} from "@/api/alerts";
import { I } from "@/components/Icons";
import { TeamsDestinations } from "@/components/TeamsDestinations";
import { Pill } from "@/components/UI";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { formatDateTimeFull } from "@/utils/format";
import { providerLabel } from "@/utils/providerLabel";

const QK = ["admin-alerts"];

export function AlertsCard() {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const { data, isLoading, error } = useQuery({
    queryKey: QK,
    queryFn: getAlertsOverview,
    refetchInterval: 60_000,
  });
  const [email, setEmail] = useState("");
  const [chName, setChName] = useState("");
  const [chUrl, setChUrl] = useState("");
  const [showHow, setShowHow] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: QK });

  const addMut = useMutation({
    mutationFn: addAlertRecipient,
    onSuccess: (row) => {
      setEmail("");
      addToast({
        title: `Added ${row.email}`,
        body: "They will get the next alert.",
      });
      void invalidate();
    },
    onError: (err) =>
      addToast({
        title: "Could not add recipient",
        body: apiErrorMessage(err, "Check the address and try again."),
        variant: "error",
      }),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      setAlertRecipientEnabled(id, enabled),
    onSuccess: () => void invalidate(),
    onError: (err) =>
      addToast({
        title: "Could not update recipient",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteAlertRecipient,
    onSuccess: () => void invalidate(),
    onError: (err) =>
      addToast({
        title: "Could not remove recipient",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const addChMut = useMutation({
    mutationFn: ({ name, url }: { name: string; url: string }) =>
      addAlertChannel(name, url),
    onSuccess: (row) => {
      setChName("");
      setChUrl("");
      addToast({
        title: `Added channel ${row.name}`,
        body: "It will get the next alert. Use Send test to confirm.",
      });
      void invalidate();
    },
    onError: (err) =>
      addToast({
        title: "Could not add channel",
        body: apiErrorMessage(err, "Check the webhook URL."),
        variant: "error",
      }),
  });
  const toggleChMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      setAlertChannelEnabled(id, enabled),
    onSuccess: () => void invalidate(),
    onError: (err) =>
      addToast({
        title: "Could not update channel",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const deleteChMut = useMutation({
    mutationFn: deleteAlertChannel,
    onSuccess: () => void invalidate(),
    onError: (err) =>
      addToast({
        title: "Could not remove channel",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const testMut = useMutation({
    mutationFn: sendAlertTest,
    onSuccess: (r) =>
      addToast({
        title: "Test alert sent",
        body: `To ${r.sent_to.join(", ")}`,
      }),
    onError: (err) =>
      addToast({
        title: "Test alert failed",
        body: apiErrorMessage(err, "The mail transport refused the message."),
        variant: "error",
      }),
  });
  const checkMut = useMutation({
    mutationFn: runAlertCheck,
    onSuccess: (overview) => {
      qc.setQueryData(QK, overview);
      addToast({
        title: "Checked every rail",
        body: "States below are current.",
      });
    },
    onError: (err) =>
      addToast({
        title: "Check failed",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });

  const onAdd = (e: FormEvent) => {
    e.preventDefault();
    const v = email.trim();
    if (v) addMut.mutate(v);
  };
  const onAddChannel = (e: FormEvent) => {
    e.preventDefault();
    if (chName.trim() && chUrl.trim())
      addChMut.mutate({ name: chName.trim(), url: chUrl.trim() });
  };
  const anyDestination =
    data != null &&
    ((data.effective_recipients.length > 0 && data.mailer !== "none") ||
      data.channels.some((c) => c.enabled));

  return (
    <div className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>Alerts</h3>
        {data && (
          <span className="dim" style={{ fontSize: 12 }}>
            {data.mailer === "none"
              ? "No mail transport configured — alerts cannot be sent"
              : `Sent via ${data.mailer === "graph" ? "Microsoft 365" : data.mailer}`}
          </span>
        )}
        <div className="spacer" />
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => checkMut.mutate()}
          disabled={checkMut.isPending}
          title="Run the availability check now"
        >
          <I.Refresh size={11} /> Check now
        </button>
        <button
          className="btn sm"
          type="button"
          onClick={() => testMut.mutate()}
          disabled={testMut.isPending || !anyDestination}
          title="Send a test to every recipient and channel"
        >
          <I.Bell size={11} /> Send test
        </button>
      </div>
      <div className="card-body">
        <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
          When a payout rail stops answering for three checks in a row, every
          recipient gets an email and every Teams channel below gets a card;
          another when it is back, and a reminder every few hours while it stays
          down.
        </p>

        {isLoading && <div className="dim">Loading…</div>}
        {error != null && (
          <div className="dim" style={{ color: "var(--warn)" }}>
            {apiErrorMessage(error, "Could not load alert settings.")}
          </div>
        )}

        {data && (
          <>
            <h4 style={{ margin: "12px 0 6px", fontSize: 12 }}>Recipients</h4>
            {data.recipients.length === 0 ? (
              <p className="dim" style={{ fontSize: 12 }}>
                {data.effective_recipients.length > 0
                  ? `Nobody added yet — alerts currently go to the deployment default (${data.effective_recipients.join(", ")}). Add an address to take over the list.`
                  : "Nobody will receive alerts. Add an address."}
              </p>
            ) : (
              <table className="table" style={{ marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map((r) => (
                    <tr key={r.id}>
                      <td className="mono">{r.email}</td>
                      <td>
                        <Pill tone={r.enabled ? "pos" : ""}>
                          {r.enabled ? "receiving" : "muted"}
                        </Pill>
                      </td>
                      <td className="dim">
                        {formatDateTimeFull(r.created_at)}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() =>
                            toggleMut.mutate({ id: r.id, enabled: !r.enabled })
                          }
                          disabled={toggleMut.isPending}
                        >
                          {r.enabled ? "Mute" : "Unmute"}
                        </button>{" "}
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(`Remove ${r.email} from alerts?`)
                            )
                              deleteMut.mutate(r.id);
                          }}
                          disabled={deleteMut.isPending}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <form
              className="row gap-8"
              style={{ alignItems: "center" }}
              onSubmit={onAdd}
            >
              <input
                className="input"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ maxWidth: 320 }}
                aria-label="Email address to add to alerts"
                required
              />
              <button
                className="btn accent sm"
                type="submit"
                disabled={addMut.isPending || email.trim() === ""}
              >
                <I.Plus size={11} /> Add recipient
              </button>
            </form>

            <h4 style={{ margin: "18px 0 6px", fontSize: 12 }}>Teams</h4>
            <TeamsDestinations onChanged={() => void invalidate()} />
            <h4 style={{ margin: "14px 0 6px", fontSize: 12 }}>
              Connected channels and chats{" "}
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => setShowHow((v) => !v)}
                style={{ marginLeft: 6 }}
              >
                {showHow ? "Hide webhook setup" : "Add by webhook instead"}
              </button>
            </h4>
            {showHow && (
              <ol
                className="dim"
                style={{ fontSize: 12, margin: "0 0 10px", paddingLeft: 18 }}
              >
                <li>
                  Use this when the Strata account cannot be added to a channel
                  (e.g. another tenant).
                </li>
                <li>
                  In Teams, open the channel → <b>⋯ → Workflows</b>.
                </li>
                <li>
                  Pick{" "}
                  <b>“Post to a channel when a webhook request is received”</b>,
                  name it (e.g. “Strata alerts”), choose the team and channel,
                  and create it.
                </li>
                <li>
                  Copy the URL Teams shows at the end and paste it below with a
                  name for the channel.
                </li>
                <li>
                  Use <b>Send test</b> — a card from the workflow should appear
                  in the channel.
                </li>
              </ol>
            )}
            {data.channels.length === 0 ? (
              <p className="dim" style={{ fontSize: 12 }}>
                No channels or chats connected.
              </p>
            ) : (
              <table className="table" style={{ marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th>Destination</th>
                    <th>Via</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.channels.map((ch) => (
                    <tr key={ch.id}>
                      <td>{ch.name}</td>
                      <td className="dim" style={{ fontSize: 11 }}>
                        {ch.kind === "graph" ? (
                          "Strata account"
                        ) : (
                          <span className="mono">{ch.webhook_ref}</span>
                        )}
                      </td>
                      <td>
                        <Pill tone={ch.enabled ? "pos" : ""}>
                          {ch.enabled ? "receiving" : "muted"}
                        </Pill>
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() =>
                            toggleChMut.mutate({
                              id: ch.id,
                              enabled: !ch.enabled,
                            })
                          }
                          disabled={toggleChMut.isPending}
                        >
                          {ch.enabled ? "Mute" : "Unmute"}
                        </button>{" "}
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(`Disconnect channel ${ch.name}?`)
                            )
                              deleteChMut.mutate(ch.id);
                          }}
                          disabled={deleteChMut.isPending}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {showHow && (
              <form
                className="row gap-8"
                style={{ alignItems: "center", flexWrap: "wrap" }}
                onSubmit={onAddChannel}
              >
                <input
                  className="input"
                  placeholder="Channel name (e.g. Ops)"
                  value={chName}
                  onChange={(e) => setChName(e.target.value)}
                  style={{ maxWidth: 200 }}
                  aria-label="Teams channel name"
                  required
                />
                <input
                  className="input"
                  type="url"
                  placeholder="https://….logic.azure.com/workflows/…"
                  value={chUrl}
                  onChange={(e) => setChUrl(e.target.value)}
                  style={{ flex: 1, minWidth: 260 }}
                  aria-label="Teams Workflow webhook URL"
                  required
                />
                <button
                  className="btn accent sm"
                  type="submit"
                  disabled={
                    addChMut.isPending ||
                    chName.trim() === "" ||
                    chUrl.trim() === ""
                  }
                >
                  <I.Plus size={11} /> Connect channel
                </button>
              </form>
            )}

            <h4 style={{ margin: "18px 0 6px", fontSize: 12 }}>
              Rails being watched
            </h4>
            {data.states.length === 0 ? (
              <p className="dim" style={{ fontSize: 12 }}>
                No checks have run yet. The first happens a minute after the
                service starts — or use “Check now”.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Rail</th>
                    <th>Status</th>
                    <th>Since</th>
                    <th>Last check</th>
                  </tr>
                </thead>
                <tbody>
                  {data.states.map((s, i) => (
                    <tr key={i}>
                      <td>{providerLabel(s.provider)}</td>
                      <td>
                        <Pill tone={s.status === "ok" ? "pos" : "neg"}>
                          {s.status === "ok" ? "responding" : "unavailable"}
                        </Pill>
                      </td>
                      <td className="dim">{formatDateTimeFull(s.since)}</td>
                      <td
                        className="dim"
                        title={formatDateTimeFull(s.last_checked_at)}
                      >
                        {s.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
